import { keyframes } from '@mui/system';

// Glass morphism container styles
export const glassContainer = {
  background: 'rgba(15, 51, 89, 0.4)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)', // Safari support
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 3,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  overflow: 'hidden',
};

// Glass morphism text field styles
export const glassTextField = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(0, 51, 78, 0.3)',
    color: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(10, 124, 160, 0.3)',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 51, 78, 0.4)',
      '& fieldset': {
        borderColor: 'rgba(10, 124, 160, 0.5)',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(0, 51, 78, 0.5)',
      '& fieldset': {
        borderColor: '#0a7ca0',
        borderWidth: '2px',
        boxShadow: '0 0 0 2px rgba(10, 124, 160, 0.2)',
      },
    },
    '& input::placeholder, & textarea::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
    // Glassmorphic scrollbar styles for multiline fields
    '& textarea': {
      // Webkit browsers (Chrome, Safari, Edge)
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 51, 78, 0.2)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(10, 124, 160, 0.4)',
        borderRadius: '4px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        '&:hover': {
          background: 'rgba(10, 124, 160, 0.6)',
        },
      },
      '&::-webkit-scrollbar-corner': {
        background: 'rgba(0, 51, 78, 0.2)',
      },
      // Firefox
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(10, 124, 160, 0.4) rgba(0, 51, 78, 0.2)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#0a7ca0',
    },
  },
  '& .MuiFormHelperText-root': {
    color: 'rgba(255, 255, 255, 0.6)',
  },
};

// Glass morphism select/dropdown styles
export const glassSelect = {
  ...glassTextField,
  '& .MuiSelect-select': {
    textAlign: 'left',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
};

// White-themed select for better dropdown appearance
export const whiteSelect = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f5f8fa',
    color: '#212121',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(10, 124, 160, 0.3)',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(10, 124, 160, 0.5)',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#0a7ca0',
        borderWidth: '2px',
        boxShadow: '0 0 0 2px rgba(10, 124, 160, 0.2)',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: '#546e7a',
    '&.Mui-focused': {
      color: '#0a7ca0',
    },
  },
  '& .MuiSelect-icon': {
    color: '#546e7a',
  },
};

// Mode toggle switch styles
export const glassToggle = {
  background: 'rgba(0, 51, 78, 0.4)',
  backdropFilter: 'blur(15px)',
  WebkitBackdropFilter: 'blur(15px)',
  border: '1px solid rgba(10, 124, 160, 0.3)',
  borderRadius: '50px',
  padding: '4px',
  '.MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    borderRadius: '46px',
    padding: '8px 20px',
    transition: 'all 0.3s ease',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(10, 124, 160, 0.1)',
    },
    '&.Mui-selected': {
      background: 'rgba(10, 124, 160, 0.6)',
      color: 'white',
      '&:hover': {
        background: 'rgba(10, 124, 160, 0.7)',
      },
    },
  },
};

// Glass button styles
export const glassButton = {
  background: 'rgba(10, 124, 160, 0.5)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(10, 124, 160, 0.6)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(10, 124, 160, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    background: 'rgba(0, 51, 78, 0.3)',
    color: 'rgba(255, 255, 255, 0.5)',
  },
};

// Preset card styles
export const glassPresetCard = {
  background: 'rgba(0, 51, 78, 0.3)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(10, 124, 160, 0.2)',
  borderRadius: 2,
  padding: 2,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(0, 51, 78, 0.5)',
    borderColor: 'rgba(10, 124, 160, 0.4)',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(10, 124, 160, 0.2)',
  },
  '&.selected': {
    background: 'rgba(10, 124, 160, 0.4)',
    borderColor: '#0a7ca0',
  },
};

// Animation keyframes
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideSwitch = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(10, 124, 160, 0.3);
  }
  50% {
    box-shadow: 0 0 25px rgba(10, 124, 160, 0.6);
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Helper function for staggered animations
export const getStaggeredAnimation = (index: number, baseDelay: number = 0) => ({
  animation: `${fadeInUp} 0.6s ease-out forwards`,
  animationDelay: `${baseDelay + index * 100}ms`,
  opacity: 0,
});